/**
 * SlashCommandMenu — dropdown that appears above the composer when the
 * user types "/" as the first character. Lifted from the AEP Chat Agent's
 * input-hints.js pattern but rewritten as a React component so it can
 * share state with the DesignView composer (mode flags, attachments).
 *
 * Usage: render unconditionally; component returns null unless `query`
 * is non-null. The parent owns the query string and selection callback.
 *
 * Performance: Wrapped in React.memo to prevent re-rendering when query/commands
 * haven't changed. Internal state (active index) is managed locally and doesn't
 * defeat memoization — only prop changes trigger re-render.
 */
import * as React from 'react';

export interface SlashCommand {
  cmd: string;          // e.g. "/feedback"
  label: string;        // short display ("Report an issue")
  desc: string;         // one-line description
  /** When true, selecting this command flips the composer into a special
   * mode rather than just inserting text. The parent decides what mode
   * means (e.g. /feedback enters feedback-mode). */
  mode?: string;
}

interface SlashCommandMenuProps {
  /** What the user has typed after the leading "/" — pass null/undefined
   *  to hide the menu. Empty string = show ALL commands. */
  query: string | null;
  commands: SlashCommand[];
  onSelect: (cmd: SlashCommand) => void;
  onDismiss: () => void;
}

const SlashCommandMenu = React.memo(function SlashCommandMenu({
  query,
  commands,
  onSelect,
  onDismiss,
}: SlashCommandMenuProps) {
  const filtered = React.useMemo(() => {
    if (query == null) return [];
    const q = query.toLowerCase().trim();
    if (!q) return commands;
    return commands.filter(
      (c) =>
        c.cmd.toLowerCase().includes(q) ||
        c.label.toLowerCase().includes(q) ||
        c.desc.toLowerCase().includes(q),
    );
  }, [query, commands]);

  const [active, setActive] = React.useState(0);

  // Reset active index whenever the filter changes shape.
  React.useEffect(() => {
    setActive(0);
  }, [filtered.length, query]);

  // Keyboard nav via document listener — the textarea's keydown handler
  // is in DesignView; it needs to call into us via imperative ref. To
  // keep this component drop-in we listen at the document level when
  // the menu is open and stopPropagation on the events we consume.
  React.useEffect(() => {
    if (query == null || filtered.length === 0) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setActive((i) => (i + 1) % filtered.length);
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setActive((i) => (i - 1 + filtered.length) % filtered.length);
      } else if (e.key === 'Tab') {
        e.preventDefault();
        onSelect(filtered[active]);
      } else if (e.key === 'Enter' && !e.shiftKey) {
        // Any Enter (plain or Cmd/Ctrl + Enter) selects the active
        // command while the menu is open — otherwise Cmd+Enter would
        // send a half-typed "/" message right past us.
        e.preventDefault();
        e.stopPropagation();
        onSelect(filtered[active]);
      } else if (e.key === 'Escape') {
        e.preventDefault();
        onDismiss();
      }
    };
    document.addEventListener('keydown', onKey, true);
    return () => document.removeEventListener('keydown', onKey, true);
  }, [query, filtered, active, onSelect, onDismiss]);

  if (query == null || filtered.length === 0) return null;

  return (
    <div
      className="slash-cmd-menu"
      style={{
        position: 'absolute',
        bottom: 'calc(100% + 6px)',
        left: 0,
        right: 0,
        background: 'var(--surface-elevated, #FFFFFF)',
        border: '1px solid var(--border, #E1E1E4)',
        borderRadius: 10,
        boxShadow: '0 8px 24px rgba(0,0,0,0.10)',
        zIndex: 20,
        overflow: 'hidden',
        maxHeight: 360,
        display: 'flex',
        flexDirection: 'column',
      }}
      // Block click-bubble — clicking the menu shouldn't blur the textarea.
      onMouseDown={(e) => e.preventDefault()}
    >
      <div style={{ flex: 1, overflowY: 'auto' }}>
        {filtered.map((c, idx) => (
          <div
            key={c.cmd}
            className="slash-cmd-item"
            onClick={() => onSelect(c)}
            onMouseEnter={() => setActive(idx)}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 12,
              padding: '10px 14px',
              cursor: 'pointer',
              background:
                idx === active
                  ? 'var(--surface-hover, rgba(26,115,232,0.06))'
                  : 'transparent',
              borderLeft:
                idx === active
                  ? '2px solid var(--accent-blue, #1A73E8)'
                  : '2px solid transparent',
              transition: 'background 80ms ease, border-left 80ms ease',
            }}
          >
            <div style={{ flex: 1, minWidth: 0 }}>
              <div
                style={{
                  fontSize: 13,
                  fontWeight: 600,
                  color: 'var(--text-primary, #1A1A1F)',
                  marginBottom: 2,
                }}
              >
                {c.label}
              </div>
              <div
                style={{
                  fontSize: 12,
                  color: 'var(--text-secondary, rgba(0,0,0,0.65))',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {c.desc}
              </div>
            </div>
            <kbd
              style={{
                fontFamily:
                  'ui-monospace, SFMono-Regular, Menlo, Monaco, "SF Mono", monospace',
                fontSize: 11.5,
                fontWeight: 500,
                color: 'var(--text-tertiary, rgba(0,0,0,0.5))',
                padding: '2px 6px',
                border: '1px solid var(--border, #E1E1E4)',
                borderRadius: 5,
                background: 'transparent',
                flexShrink: 0,
              }}
            >
              {c.cmd}
            </kbd>
          </div>
        ))}
      </div>
      <div
        style={{
          borderTop: '1px solid var(--border, #E1E1E4)',
          padding: '8px 14px',
          background: 'var(--surface-2, #FAFAFA)',
          fontSize: 11,
          color: 'var(--text-tertiary, rgba(0,0,0,0.5))',
          textAlign: 'center',
        }}
      >
        ↑↓ navigate  ·  ↵ select  ·  esc dismiss
      </div>
    </div>
  );
});

export default SlashCommandMenu;

/**
/**
 * Shared command registry. New commands plug in here and become
 * available in every composer that mounts SlashCommandMenu.
 */
export const SLASH_COMMANDS: SlashCommand[] = [
  {
    cmd: '/feedback',
    label: 'Report an issue or idea',
    desc: 'Describe it, paste screenshots — goes straight to the team',
    mode: 'feedback',
  },
  {
    cmd: '/hours-saved',
    label: 'Log time saved',
    desc: 'e.g. /hours-saved 4 — tell us how many hours this design saved',
    mode: 'hours-saved',
  },
  {
    cmd: '/auto-run',
    label: 'Run the whole pipeline',
    desc: 'Auto-continues every stage through QA and export',
    mode: 'auto-run',
  },
  {
    cmd: '/export',
    label: 'Export the design',
    desc: 'HTML, DOCX or JSON',
    mode: 'export',
  },
  {
    cmd: '/share',
    label: 'Copy a share link',
    desc: 'Share this design with your team',
    mode: 'share',
  },
  {
    cmd: '/help',
    label: 'Show all commands',
    desc: 'View keyboard shortcuts and all slash commands',
    mode: 'help',
  },
];
